import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { CreditCard, CheckCircle, AlertCircle, Loader2, Clock, ShieldCheck } from 'lucide-react';
import PayoLogo from './PayoLogo';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QRCheckoutForm = ({ requestId, amount, collectorName, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(error.message);
        setIsProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        const res = await fetch(`${BACKEND_URL}/api/payments/requests/${requestId}/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        onSuccess();
      } else {
        setErrorMessage('Payment could not be completed.');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setErrorMessage('An unexpected error occurred.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="min-h-[200px]">
        <PaymentElement />
      </div>

      {errorMessage && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="qr-pay-error">
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <Button
        type="submit"
        disabled={isProcessing || !stripe || !elements}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-base"
        data-testid="qr-pay-submit"
      >
        {isProcessing ? (
          <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
        ) : (
          <><CreditCard className="w-5 h-5 mr-2" /> Pay ${(amount / 100).toFixed(2)}</>
        )}
      </Button>

      <div className="flex items-center justify-center space-x-2 text-xs text-gray-400">
        <ShieldCheck className="w-3 h-3" />
        <span>Secured by Stripe</span>
      </div>
    </form>
  );
};

const QRPayPage = () => {
  const { requestId } = useParams();
  const [details, setDetails] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [pageState, setPageState] = useState('loading');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    fetchDetails();
  }, [requestId]);

  const fetchDetails = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/payments/requests/${requestId}`);
      if (res.ok) {
        const data = await res.json();
        setDetails(data);
        setPageState('ready');
      } else if (res.status === 410) {
        const err = await res.json();
        setErrorMsg(err.detail || 'This payment link has expired or been used');
        setPageState('expired');
      } else {
        setErrorMsg('Payment link not found');
        setPageState('error');
      }
    } catch {
      setErrorMsg('Unable to load payment details');
      setPageState('error');
    }
  };

  const handlePayNow = async () => {
    setPageState('paying');
    try {
      const res = await fetch(`${BACKEND_URL}/api/payments/requests/${requestId}/create-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (res.ok) {
        const data = await res.json();
        setClientSecret(data.client_secret);
      } else if (res.status === 410) {
        setPageState('expired');
        setErrorMsg('This payment link has expired or been used');
      } else {
        const err = await res.json();
        setErrorMsg(err.detail || 'Failed to start payment');
        setPageState('error');
      }
    } catch {
      setErrorMsg('Failed to connect');
      setPageState('error');
    }
  };

  const appearance = {
    theme: 'stripe',
    variables: { colorPrimary: '#2563eb', borderRadius: '8px' },
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-sm w-full shadow-lg" data-testid="qr-pay-page">
        <CardContent className="pt-8 pb-6 px-6">
          <div className="flex items-center justify-center mb-6">
            <PayoLogo size="md" />
          </div>

          {pageState === 'loading' && (
            <div className="flex flex-col items-center py-12" data-testid="qr-pay-loading">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-3" />
              <p className="text-sm text-gray-500">Loading payment...</p>
            </div>
          )}

          {pageState === 'ready' && details && (
            <div className="space-y-5" data-testid="qr-pay-ready">
              <div className="text-center space-y-2">
                <p className="text-sm text-gray-500">{details.group_name}</p>
                <p className="text-4xl font-bold text-gray-900">
                  ${(details.amount_cents / 100).toFixed(2)}
                </p>
                <p className="text-base text-gray-700">
                  Pay <span className="font-semibold">{details.collector_name}</span>
                </p>
              </div>
              <Button
                onClick={handlePayNow}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-base"
                data-testid="qr-pay-now-btn"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Pay Now
              </Button>
              <div className="flex items-center justify-center space-x-2 text-xs text-gray-400">
                <ShieldCheck className="w-3 h-3" />
                <span>Secured by Stripe</span>
              </div>
            </div>
          )}

          {pageState === 'paying' && !clientSecret && (
            <div className="flex flex-col items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-3" />
              <p className="text-sm text-gray-500">Preparing payment...</p>
            </div>
          )}

          {pageState === 'paying' && clientSecret && details && (
            <div data-testid="qr-pay-form">
              <div className="text-center mb-4">
                <p className="text-sm text-gray-500">{details.group_name}</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${(details.amount_cents / 100).toFixed(2)}
                </p>
                <p className="text-sm text-gray-600">to {details.collector_name}</p>
              </div>
              <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
                <QRCheckoutForm
                  requestId={requestId}
                  amount={details.amount_cents}
                  collectorName={details.collector_name}
                  onSuccess={() => setPageState('success')}
                />
              </Elements>
            </div>
          )}

          {pageState === 'success' && (
            <div className="text-center py-8" data-testid="qr-pay-success">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <p className="text-xl font-bold text-green-700">Payment Complete!</p>
              {details && (
                <p className="text-sm text-gray-500 mt-2">
                  You paid ${(details.amount_cents / 100).toFixed(2)} to {details.collector_name}
                </p>
              )}
              <p className="text-xs text-gray-400 mt-4">You can close this page</p>
            </div>
          )}

          {pageState === 'expired' && (
            <div className="text-center py-8" data-testid="qr-pay-expired">
              <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-lg font-semibold text-gray-700">Link Expired</p>
              <p className="text-sm text-gray-500 mt-2">{errorMsg}</p>
              <p className="text-xs text-gray-400 mt-4">Ask the collector to generate a new QR</p>
            </div>
          )}

          {pageState === 'error' && (
            <div className="text-center py-8" data-testid="qr-pay-error-page">
              <AlertCircle className="w-12 h-12 text-red-300 mx-auto mb-3" />
              <p className="text-lg font-semibold text-gray-700">Something went wrong</p>
              <p className="text-sm text-gray-500 mt-2">{errorMsg}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QRPayPage;
